import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, finalize } from 'rxjs';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ProgressSpinner } from 'primeng/progressspinner';

import { ComposicaoExcluirApiRequest, ComposicaoSalvarApiRequest, TipoComposicao } from '@/core/api/sicom-api/registro/orgao/composicao.model';
import { PermissaoService } from '@/core/api/sicom-api/portal/permissao/permissao.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { CargoOpcao, ComposicaoLinha, ComposicaoService } from '@/features/registro/orgaos/dashboard-orgao/components/composicao/composicao.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/** Controles do diálogo de edição/cadastro de membro da composição. */
interface ComposicaoEdicaoForm {
    crm: FormControl<string | null>;
    uf: FormControl<string | null>;
    nome: FormControl<string | null>;
    cargo: FormControl<number | null>;
    dataEntrada: FormControl<Date | null>;
    dataSaida: FormControl<Date | null>;
}

@Component({
    selector: 'app-composicao',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        FormsModule,
        ReactiveFormsModule,
        SelectButtonModule,
        CardContent,
        CustomTableComponent,
        DialogModule,
        SelectModule,
        InputTextModule,
        DatePickerModule,
        ButtonModule,
        ConfirmDialogModule,
        ProgressSpinner,
        CanDirective
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './composicao.html',
    styleUrl: './composicao.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class Composicao implements OnInit {
    uf = input<string>('DF');
    pessoa = input<Pessoa>();
    usarEndpointsDelegacia = input(false);
    dadosAtualizados = output<void>();

    protected readonly PERMISSOES = PERMISSOES;

    readonly visualizandoAntigos = signal(false);
    readonly carregando = signal(false);
    readonly composicoes = signal<ComposicaoLinha[]>([]);
    readonly opcoesTipoComposicao = [
        { label: 'Atual', value: false },
        { label: 'Antiga', value: true }
    ];

    readonly exibirDialogEdicao = signal(false);
    readonly salvandoEdicao = signal(false);
    readonly modoCadastro = signal(false);
    readonly membroSelecionado = signal<ComposicaoLinha | null>(null);
    formularioEdicao!: FormGroup<ComposicaoEdicaoForm>;

    cargos: Array<{ label: string; value: number }> = [];
    private readonly mapaCargoPorDescricao = new Map<string, number>();

    readonly metadata: TableMetadata = {
        headers: {
            uf: { type: 'text', value: 'UF', hidden: false, default: '-' },
            nome: { type: 'text', value: 'Nome', hidden: false, default: '-' },
            cargo: { type: 'text', value: 'Cargo', hidden: false, default: '-' },
            dataEntrada: { type: 'text', value: 'Entrada', hidden: false, default: '-' },
            dataSaida: { type: 'text', value: 'Saída', hidden: false, default: '-' }
        },
        title: 'nome',
        actions: {
            editar: {
                label: 'Editar',
                icon: 'pi pi-pencil',
                color: 'primary',
                permission: PERMISSOES.orgaos.composicao.editar,
                command: (item: ComposicaoLinha) => void this.abrirEdicao(item)
            },
            excluir: {
                label: 'Excluir',
                icon: 'pi pi-trash',
                color: 'danger',
                permission: PERMISSOES.orgaos.composicao.excluir,
                command: (item: ComposicaoLinha) => void this.confirmarExclusao(item)
            }
        }
    };

    readonly ordemColunas = ['uf', 'nome', 'cargo', 'dataEntrada', 'dataSaida'];

    private readonly inicializado = signal(false);
    private readonly destroyRef = inject(DestroyRef);
    private readonly permissaoService = inject(PermissaoService);
    readonly temPermissaoEdicao = signal(false);

    constructor(
        private fb: FormBuilder,
        private composicaoService: ComposicaoService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService
    ) {
        effect(() => {
            if (!this.inicializado()) {
                return;
            }

            this.pessoa();
            this.uf();
            this.carregarComposicao();
        });

        effect(() => {
            const uf = this.ufParaPermissao();
            if (!uf) {
                this.temPermissaoEdicao.set(false);
                return;
            }
            this.permissaoService
                .verificar('mesmaUf', uf)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((pode) => this.temPermissaoEdicao.set(pode));
        });
    }

    ngOnInit(): void {
        this.formularioEdicao = this.fb.group({
            crm: [{ value: '', disabled: true }],
            uf: [{ value: '', disabled: true }, Validators.required],
            nome: [{ value: '', disabled: true }, Validators.required],
            cargo: [null as number | null, Validators.required],
            dataEntrada: [null as Date | null, Validators.required],
            dataSaida: [null as Date | null]
        });

        this.carregarCargos();
        this.inicializado.set(true);
    }

    carregarComposicao(): void {
        const pessoaSelecionada = this.pessoa?.();
        const ufSelecionada = (this.uf() || '').trim().toUpperCase();

        if (!ufSelecionada && !pessoaSelecionada?.id) {
            this.composicoes.set([]);
            return;
        }

        this.carregando.set(true);
        const observavel =
            pessoaSelecionada?.id && pessoaSelecionada.uf ? this.composicaoService.buscarPorPessoa(pessoaSelecionada, this.tipoComposicaoSelecionado()) : this.composicaoService.buscarPorUf(ufSelecionada, this.tipoComposicaoSelecionado());

        observavel
            .pipe(
                finalize(() => {
                    this.carregando.set(false);
                })
            )
            .subscribe({
                next: (result) => {
                    this.composicoes.set(result);
                },
                error: () => {
                    this.composicoes.set([]);
                }
            });
    }

    onTipoComposicaoChange(visualizandoAntigos: boolean): void {
        this.visualizandoAntigos.set(visualizandoAntigos);
        this.carregarComposicao();
    }

    carregamentoInicialSemDados(): boolean {
        return this.carregando() && this.composicoes().length === 0;
    }

    async abrirCadastro(): Promise<void> {
        if (!(await this.podeAlterarComposicao())) {
            this.notificarSemPermissao();
            return;
        }

        const contextoSolicitanteOrgao = this.contextoSolicitanteOrgao();
        const ufPadrao = contextoSolicitanteOrgao?.uf || (this.uf() || '').trim().toUpperCase();

        this.modoCadastro.set(true);
        this.membroSelecionado.set(null);

        this.formularioEdicao.reset();
        this.formularioEdicao.patchValue(
            {
                crm: '',
                uf: ufPadrao,
                nome: '',
                cargo: null,
                dataEntrada: null,
                dataSaida: null
            },
            { emitEvent: false }
        );

        this.formularioEdicao.get('crm')?.enable({ emitEvent: false });
        this.formularioEdicao.get('crm')?.setValidators([Validators.required]);
        this.formularioEdicao.get('crm')?.updateValueAndValidity({ emitEvent: false });

        this.exibirDialogEdicao.set(true);
    }

    async abrirEdicao(item: ComposicaoLinha): Promise<void> {
        if (!(await this.podeAlterarComposicao())) {
            this.notificarSemPermissao();
            return;
        }

        const idCargo = this.idCargoPorDescricao(item.cargo);
        if (!idCargo) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Não foi possível identificar o cargo do membro para edição.'
            });
            return;
        }

        this.modoCadastro.set(false);
        this.membroSelecionado.set(item);
        this.formularioEdicao.patchValue(
            {
                crm: '',
                uf: item.ufMedico || item.uf || '',
                nome: item.nome || '',
                cargo: idCargo,
                dataEntrada: this.isoParaDate(item.dataEntradaIso),
                dataSaida: this.isoParaDate(item.dataSaidaIso)
            },
            { emitEvent: false }
        );
        this.formularioEdicao.get('crm')?.disable({ emitEvent: false });
        this.formularioEdicao.get('crm')?.clearValidators();
        this.formularioEdicao.get('crm')?.updateValueAndValidity({ emitEvent: false });

        this.exibirDialogEdicao.set(true);
    }

    async salvarEdicao(): Promise<void> {
        if (!this.modoCadastro() && !this.membroSelecionado()) {
            return;
        }

        if (this.formularioEdicao.invalid) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Preencha os campos obrigatórios para salvar.'
            });
            return;
        }

        if (!(await this.podeAlterarComposicao())) {
            this.notificarSemPermissao();
            return;
        }

        const contextoSolicitanteOrgao = this.contextoSolicitanteOrgao();
        if (!contextoSolicitanteOrgao) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        const dataEntradaIso = this.dateParaIso(this.formularioEdicao.get('dataEntrada')?.value || null);
        if (!dataEntradaIso) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Data de entrada inválida.'
            });
            return;
        }

        const dataSaidaIso = this.dateParaIso(this.formularioEdicao.get('dataSaida')?.value || null);
        const crmCadastro = (this.formularioEdicao.get('crm')?.value || '').toString().trim();
        const crmCadastroNumero = Number(crmCadastro);
        const ufMedicoCadastro = (this.formularioEdicao.get('uf')?.value || '').toString().trim().toUpperCase();

        if (this.modoCadastro() && (!Number.isFinite(crmCadastroNumero) || crmCadastroNumero <= 0 || !ufMedicoCadastro)) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Informe um CRM válido para adicionar o membro.'
            });
            return;
        }

        let idMedico = this.modoCadastro() ? crmCadastroNumero : this.membroSelecionado()!.idMedico;
        let ufMedico = this.modoCadastro() ? ufMedicoCadastro : this.membroSelecionado()!.ufMedico;

        if (this.modoCadastro()) {
            const medicoPorCrm = await firstValueFrom(this.composicaoService.buscarMedicoPorCrm(crmCadastro, ufMedicoCadastro));
            if (!medicoPorCrm) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Nenhum médico foi encontrado para o CRM informado na UF selecionada.'
                });
                return;
            }

            idMedico = medicoPorCrm.idMedico;
            ufMedico = medicoPorCrm.ufMedico;
        }

        const payload: ComposicaoSalvarApiRequest = {
            id: contextoSolicitanteOrgao.id,
            uf: contextoSolicitanteOrgao.uf,
            idMedico,
            ufMedico,
            cargo: Number(this.formularioEdicao.get('cargo')?.value),
            dataEntrada: dataEntradaIso,
            dataSaida: dataSaidaIso
        };

        this.salvandoEdicao.set(true);
        const requisicaoSalvar = this.usarEndpointsDelegacia() ? this.composicaoService.salvarDelegacia(payload) : this.composicaoService.salvar(payload);

        requisicaoSalvar
            .pipe(
                finalize(() => {
                    this.salvandoEdicao.set(false);
                })
            )
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: this.modoCadastro() ? 'Membro adicionado com sucesso.' : 'Membro atualizado com sucesso.'
                    });
                    this.exibirDialogEdicao.set(false);
                    this.carregarComposicao();
                    this.emitirDadosAtualizados();
                },
                error: (err) => {
                    const mensagem = err instanceof Error ? err.message : this.modoCadastro() ? 'Erro ao adicionar membro da composição.' : 'Erro ao atualizar membro da composição.';
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: mensagem
                    });
                }
            });
    }

    async confirmarExclusao(item: ComposicaoLinha): Promise<void> {
        if (!(await this.podeAlterarComposicao())) {
            this.notificarSemPermissao();
            return;
        }

        const contextoSolicitanteOrgao = this.contextoSolicitanteOrgao();
        if (!contextoSolicitanteOrgao) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        const idCargo = this.idCargoPorDescricao(item.cargo);
        const dataEntradaIso = item.dataEntradaIso || this.dataBrParaIso(item.dataEntrada || '');

        if (!idCargo || !dataEntradaIso) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Não foi possível identificar os dados do membro para exclusão.'
            });
            return;
        }

        const payload: ComposicaoExcluirApiRequest = {
            id: contextoSolicitanteOrgao.id,
            uf: contextoSolicitanteOrgao.uf,
            idMedico: item.idMedico,
            ufMedico: item.ufMedico,
            cargo: idCargo,
            dataEntrada: dataEntradaIso
        };

        this.confirmationService.confirm({
            message: `Deseja realmente excluir o membro ${item.nome}?`,
            header: 'Excluir Membro',
            icon: 'pi pi-trash',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => {
                const requisicaoExclusao = this.usarEndpointsDelegacia() ? this.composicaoService.excluirDelegacia(payload) : this.composicaoService.excluir(payload);

                requisicaoExclusao.subscribe({
                    next: (removido) => {
                        if (!removido) {
                            this.messageService.add({
                                severity: 'warn',
                                summary: 'Atenção',
                                detail: 'O membro não foi encontrado para exclusão.'
                            });
                            return;
                        }

                        this.messageService.add({
                            severity: 'success',
                            summary: 'Sucesso',
                            detail: 'Membro excluído com sucesso.'
                        });
                        this.carregarComposicao();
                        this.emitirDadosAtualizados();
                    },
                    error: (err) => {
                        const mensagem = err instanceof Error ? err.message : 'Erro ao excluir membro da composição.';
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erro',
                            detail: mensagem
                        });
                    }
                });
            }
        });
    }

    private carregarCargos(): void {
        this.composicaoService.listarCargos().subscribe({
            next: (cargos) => {
                this.configurarCargos(cargos);
            },
            error: () => {
                this.cargos = [];
                this.mapaCargoPorDescricao.clear();
            }
        });
    }

    private configurarCargos(cargos: CargoOpcao[]): void {
        const ordenados = [...(cargos || [])].sort((a, b) => a.descricao.localeCompare(b.descricao));
        this.cargos = ordenados.map((cargo) => ({
            label: cargo.descricao,
            value: cargo.id
        }));

        this.mapaCargoPorDescricao.clear();
        ordenados.forEach((cargo) => {
            this.mapaCargoPorDescricao.set(this.normalizarTexto(cargo.descricao), cargo.id);
        });
    }

    private idCargoPorDescricao(cargo: string): number | null {
        if (!cargo) {
            return null;
        }

        const id = this.mapaCargoPorDescricao.get(this.normalizarTexto(cargo));
        return typeof id === 'number' ? id : null;
    }

    private async podeAlterarComposicao(): Promise<boolean> {
        const ufValidacao = this.ufParaPermissao();
        if (!ufValidacao) {
            return false;
        }

        try {
            return await firstValueFrom(this.permissaoService.verificar('mesmaUf', ufValidacao));
        } catch {
            return false;
        }
    }

    private ufParaPermissao(): string | null {
        const contextoSolicitanteOrgao = this.contextoSolicitanteOrgao();
        if (contextoSolicitanteOrgao?.uf) {
            return contextoSolicitanteOrgao.uf;
        }

        const ufSelecionada = (this.uf() || '').trim().toUpperCase();
        return ufSelecionada || null;
    }

    private contextoSolicitanteOrgao(): Pessoa | null {
        const candidato = this.pessoa?.();
        if (!candidato) {
            return null;
        }

        const id = Number(candidato.id);
        const uf = (candidato.uf || '').toString().trim().toUpperCase();
        if (!Number.isFinite(id) || id <= 0 || !uf) {
            return null;
        }

        return { id, uf };
    }

    private tipoComposicaoSelecionado(): TipoComposicao {
        return this.visualizandoAntigos() ? 'ANTIGA' : 'ATUAL';
    }

    private normalizarTexto(valor: string): string {
        return valor
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toUpperCase();
    }

    private dataBrParaIso(dataBr: string): string | null {
        const valor = (dataBr || '').trim();
        if (!valor) {
            return null;
        }

        const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(valor);
        if (!match) {
            return null;
        }

        const dia = Number(match[1]);
        const mes = Number(match[2]);
        const ano = Number(match[3]);
        const data = new Date(ano, mes - 1, dia);
        if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
            return null;
        }

        return `${String(ano).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    }

    private isoParaDate(dataIso: string | null): Date | null {
        if (!dataIso) {
            return null;
        }

        const partes = dataIso.split('-');
        if (partes.length !== 3) {
            return null;
        }

        const [ano, mes, dia] = partes;
        if (!ano || !mes || !dia) {
            return null;
        }

        const data = new Date(Number(ano), Number(mes) - 1, Number(dia));
        return Number.isNaN(data.getTime()) ? null : data;
    }

    private dateParaIso(data: Date | null): string | null {
        if (!(data instanceof Date) || Number.isNaN(data.getTime())) {
            return null;
        }

        const ano = data.getFullYear();
        const mes = data.getMonth() + 1;
        const dia = data.getDate();
        return `${String(ano).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    }

    private notificarSemPermissao(): void {
        this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Você não possui permissão para alterar a composição desta UF.'
        });
    }

    private notificarContextoEscritaInvalido(): void {
        this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Não foi possível identificar o contexto solicitante do órgão para salvar alterações nesta aba.'
        });
    }

    private emitirDadosAtualizados(): void {
        this.dadosAtualizados.emit();
    }

    get tituloDialogComposicao(): string {
        return this.modoCadastro() ? 'Adicionar Membro' : 'Editar Membro';
    }

    get labelBotaoSalvar(): string {
        return this.modoCadastro() ? 'Adicionar' : 'Salvar';
    }
}
